import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle, KeyRound, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Account() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error(t("account.tooShort"));
    if (form.next !== form.confirm) return toast.error(t("account.mismatch"));
    if (form.next === form.current) return toast.error(t("account.sameAsOld"));
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success(t("account.updated"));
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err));
    }
    setSaving(false);
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success(t("account.deleted"));
      navigate("/", { replace: true });
      logout();
    } catch (err) {
      toast.error(apiError(err));
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-5 py-12 lg:py-16">
        <p className="label-eyebrow">{t("account.title")}</p>
        <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="account-title">
          {t("account.subtitle")}
        </h1>

        <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6 ambient" data-testid="account-info">
          <p className="flex items-center gap-2 label-eyebrow">
            <UserCircle size={15} className="text-sea" /> {t("account.title")}
          </p>
          <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-3">
            <div className="min-w-0">
              <dt className="text-xs text-slate-500">{t("account.name")}</dt>
              <dd className="mt-0.5 truncate font-medium text-slate-900" data-testid="account-name">{user?.name || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs text-slate-500">{t("account.email")}</dt>
              <dd className="mt-0.5 break-all font-medium text-slate-900" data-testid="account-email">{user?.email || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs text-slate-500">{t("account.role")}</dt>
              <dd className="mt-0.5 font-medium capitalize text-slate-900" data-testid="account-role">{user?.role || "—"}</dd>
            </div>
          </dl>
        </section>

        <form onSubmit={submit} className="mt-6 rounded-xl border border-slate-200 bg-white p-6 ambient" data-testid="change-password-form">
          <p className="flex items-center gap-2 label-eyebrow">
            <KeyRound size={15} className="text-sea" /> {t("account.changePassword")}
          </p>
          <div className="mt-5 space-y-4">
            <div>
              <Label htmlFor="cur">{t("account.current")}</Label>
              <Input id="cur" type="password" required autoComplete="current-password" data-testid="current-password-input" value={form.current} onChange={set("current")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="new">{t("account.new")}</Label>
              <Input id="new" type="password" required autoComplete="new-password" data-testid="new-password-input" value={form.next} onChange={set("next")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="conf">{t("account.confirm")}</Label>
              <Input id="conf" type="password" required autoComplete="new-password" data-testid="confirm-password-input" value={form.confirm} onChange={set("confirm")} className="mt-1.5" />
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="change-password-btn" className="mt-6 w-full bg-sea hover:bg-sea-dark">
            {saving ? t("common.loading") : t("account.save")}
          </Button>
        </form>

        <section className="mt-6 rounded-xl border border-rose-200 bg-rose-50/40 p-6" data-testid="account-danger-zone">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-rose-600">
            <Trash2 size={15} /> {t("account.dangerZone")}
          </p>
          <p className="mt-3 text-sm text-slate-600" data-testid="delete-account-desc">{t("account.deleteDesc")}</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={deleting}
                data-testid="delete-account-btn"
                className="mt-4 border-rose-300 text-rose-600 hover:bg-rose-100 hover:text-rose-700"
              >
                {deleting ? t("account.deleting") : t("account.deleteBtn")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="delete-account-dialog-title">{t("account.deleteConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("account.deleteConfirmDesc")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-account-cancel-btn">{t("account.deleteCancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={deleteAccount}
                  disabled={deleting}
                  data-testid="delete-account-confirm-btn"
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  {deleting ? t("account.deleting") : t("account.deleteConfirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      </div>
      <Footer />
    </div>
  );
}
